/**
 * Request Changes Dialog
 * Allows homeowners to request changes on a BOQ — Studio theme
 */

import { MessageSquare, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import {
	type BOQ,
	createBOQChangeRequest,
} from "../../services/boq/boq-api.service";

interface RequestChangesDialogProps {
	boq: BOQ;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

export function RequestChangesDialog({
	boq,
	open,
	onOpenChange,
	onSuccess,
}: RequestChangesDialogProps) {
	const { t } = useTranslation();
	const [message, setMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const textInputRef = useRef<HTMLTextAreaElement>(null);

	const handleClose = useCallback(() => {
		setMessage("");
		onOpenChange(false);
	}, [onOpenChange]);

	// Focus textarea on open
	useEffect(() => {
		if (open) {
			setTimeout(() => textInputRef.current?.focus(), 80);
		}
	}, [open]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") handleClose();
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [open, handleClose]);

	const handleSubmit = async () => {
		if (!message.trim()) {
			toast.error(t("boq.changeRequests.dialog.validationError"));
			return;
		}

		setLoading(true);
		try {
			const result = await createBOQChangeRequest(boq.id, message.trim());
			if (result.success) {
				toast.success(t("boq.changeRequests.dialog.success"));
				setMessage("");
				onOpenChange(false);
				onSuccess();
			} else {
				toast.error(result.error || t("boq.changeRequests.dialog.error"));
			}
		} catch {
			toast.error(t("boq.changeRequests.dialog.error"));
		} finally {
			setLoading(false);
		}
	};

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 200,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px 16px",
				background: "rgba(30, 41, 59, 0.45)",
				backdropFilter: "blur(4px)",
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) handleClose();
			}}
			onKeyDown={(e) => {
				if (e.key === "Escape") handleClose();
			}}
		>
			<FloatPanel
				elevation={4}
				padding={0}
				style={{ width: "100%", maxWidth: 500, overflow: "hidden" }}
			>
				{/* Header */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "20px 24px 16px",
						borderBottom: "1px solid var(--color-dashboard-border)",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<MessageSquare
							size={20}
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						/>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 20,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
								margin: 0,
								letterSpacing: "-0.01em",
							}}
						>
							{t("boq.changeRequests.dialog.title")}
						</h2>
					</div>
					<button
						type="button"
						onClick={handleClose}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 6,
							border: "1px solid var(--color-dashboard-border)",
							background: "transparent",
							cursor: "pointer",
							color: "var(--color-dashboard-muted)",
							transition: "background 150ms",
						}}
						onMouseEnter={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"var(--color-dashboard-divider)")
						}
						onMouseLeave={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"transparent")
						}
					>
						<X size={15} strokeWidth={2} />
					</button>
				</div>

				{/* Body */}
				<div
					style={{
						padding: "20px 24px",
						display: "flex",
						flexDirection: "column",
						gap: 14,
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							color: "var(--color-dashboard-muted)",
							lineHeight: 1.5,
							margin: 0,
						}}
					>
						{t("boq.changeRequests.dialog.description", {
							version: boq.versionNumber,
						})}
					</p>

					<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
						<label
							htmlFor="message"
							style={{
								fontSize: 11,
								fontFamily: "var(--font-mono)",
								fontWeight: 600,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
							}}
						>
							{t("boq.changeRequests.dialog.label")}
						</label>
						<textarea
							id="message"
							ref={textInputRef}
							placeholder={t("boq.changeRequests.dialog.placeholder")}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							rows={5}
							disabled={loading}
							style={{
								width: "100%",
								padding: "10px 14px",
								fontSize: 14,
								fontFamily: "var(--font-body)",
								color: "var(--color-dashboard-text)",
								background: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 7,
								outline: "none",
								resize: "none",
								transition: "border-color 200ms, box-shadow 200ms",
								lineHeight: 1.5,
							}}
							onFocus={(e) => {
								e.currentTarget.style.borderColor =
									"var(--color-dashboard-accent)";
								e.currentTarget.style.boxShadow =
									"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
							}}
							onBlur={(e) => {
								e.currentTarget.style.borderColor =
									"var(--color-dashboard-border)";
								e.currentTarget.style.boxShadow = "none";
							}}
						/>
						<p
							style={{
								fontSize: 11,
								color: "var(--color-dashboard-muted)",
								margin: "4px 0 0 0",
							}}
						>
							{t("boq.changeRequests.dialog.hint", { role: boq.createdByRole })}
						</p>
					</div>
				</div>

				{/* Footer */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 10,
						padding: "16px 24px",
						borderTop: "1px solid var(--color-dashboard-border)",
						background: "var(--color-dashboard-surface)",
					}}
				>
					<StudioButton
						variant="secondary"
						size="sm"
						type="button"
						onClick={handleClose}
						disabled={loading}
					>
						{t("boq.changeRequests.dialog.cancel")}
					</StudioButton>
					<StudioButton
						variant="primary"
						size="sm"
						type="button"
						onClick={handleSubmit}
						disabled={loading || !message.trim()}
					>
						{loading
							? t("boq.changeRequests.dialog.sending")
							: t("boq.changeRequests.dialog.send")}
					</StudioButton>
				</div>
			</FloatPanel>
		</div>
	);
}
